/**
 * Types of the in-house translation catalogue.
 *
 * Every area keeps its English text in `messages/<area>/en.ts` (`as const`) and
 * its German and Chinese text next to it, typed `satisfies Messages<typeof en>`
 * so a missing key, a missing plural `other` form or a lost `{param}` fails
 * `bun typecheck`. Extra or misspelt params are caught by the completeness
 * test, which compares the placeholder and tag sets of every message.
 */

export const APP_LANGUAGES = ["en", "de", "zh"] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

/** A message whose wording depends on a number: `Intl.PluralRules` forms.
 *  Only `other` is required — Chinese has nothing else. */
export type PluralForms = {
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
};

export type MessageValue = string | PluralForms;
export type MessageCatalogue = { readonly [key: string]: MessageValue };

/** The `{param}` names in one message text; `{param|lower}` names `param`. */
export type ParamsOf<S> = S extends `${string}{${infer P}}${infer Rest}`
  ? (P extends `${infer Name}|${string}` ? Name : P) | ParamsOf<Rest>
  : never;

type UnionToIntersection<U> = (
  U extends unknown ? (value: U) => void : never
) extends (value: infer I) => void
  ? I
  : never;

/** A translated text that still contains every `{param}` of the English one
 *  (each language may add its own modifier, `{param|lower}`). */
type HoldsParam<P extends string> =
  | `${string}{${P}}${string}`
  | `${string}{${P}|${string}}${string}`;
// Wrapped in a tuple so each param's two spellings stay a union while the
// params themselves are intersected.
type Holding<P extends string> = [P] extends [never]
  ? string
  : FirstOf<UnionToIntersection<P extends string ? [HoldsParam<P>] : never>>;

type FirstOf<T> = T extends readonly [infer First] ? First : never;

type Translation<V> = V extends string
  ? Holding<ParamsOf<V>>
  : V extends PluralForms
    ? Omit<PluralForms, "other"> & {
        readonly other: Holding<ParamsOf<V["other"]>>;
      }
    : never;

/** The shape a German or Chinese catalogue must satisfy for English `E`. */
export type Messages<E extends MessageCatalogue> = {
  readonly [K in keyof E]: Translation<E[K]>;
};

type ValueParams<V> = V extends string
  ? ParamsOf<V>
  : V extends PluralForms
    ? ParamsOf<V[keyof V] & string> | "count"
    : never;

export type ParamValue = string | number;

/** The arguments `t(key, …)` takes for one message: none, or its params. */
export type MessageArgs<V> = [ValueParams<V>] extends [never]
  ? []
  : [
      params: {
        readonly [P in ValueParams<V>]: P extends "count" ? number : ParamValue;
      },
    ];

/** One area's catalogue handle, created once with `defineMessages`. */
export type MessageArea<E extends MessageCatalogue> = {
  readonly area: string;
  readonly en: E;
};
